import { axios } from '/plugins/axios.client';
import { errorFromResponse, encodeSingleFieldToBase64 } from '/src/common/general-utils';
import { i18n } from '/plugins/i18n.client';
const errorHandler = errorFromResponse(i18n.global.t.bind(i18n));
import { toast } from '/plugins/toaster.client.js';
import promise from 'bluebird';

/**
 * Fetch Object Details
 */
export const getObjectDetails = async (ObjectUUID) => {
  try {
    return await axios.$get(`/api/object/${ObjectUUID}`);
  } catch (error) {
    toast.error(errorHandler('common.object_record.fetch_object_details', error));
    return undefined;
  }
};

export const modifyObjectFields = (objectFields) =>
  objectFields.map((field) => {
    let settings;
    try {
      settings = JSON.parse(field.Settings);
    } catch (exception) {
      //set empty setting if not parsed
      settings = {};
    }
    let suggestions;
    try {
      suggestions = field.Suggestions
        ? field.Suggestions.sParseJSON().map((suggestion) => {
            suggestion.displayValue = suggestion.displayValue.replace(/&sbquo;/g, ',');
            suggestion.databaseValue = suggestion.databaseValue.replace(/&sbquo;/g, ',');
            return suggestion;
          })
        : null;
    } catch (exception) {
      //set empty suggestion if not parsed
      suggestions = [];
    }
    return {
      ...field,
      IsRequired: !!field.IsRequired,
      FieldType: field.IsReference ? 'REFERENCE' : field.FieldType,
      Settings: settings || {},
      Suggestions: suggestions
    };
  });

export const getRawObjectFields = async (ObjectUUID, params = {}) => {
  try {
    return await axios.$get(`/api/object/fields/list/${ObjectUUID}`, { params });
  } catch (error) {
    toast.error(errorHandler('common.object_record.fetch_object_fields', error));
    return undefined;
  }
};

/**
 * Fetch Object Field Details
 */
export const getObjectFieldDetails = async (ObjectUUID, params = {}) => {
  const objectFields = await getRawObjectFields(ObjectUUID, params);
  return objectFields ? modifyObjectFields(objectFields) : undefined;
};
export const fetchObjectUUIDFields = async (objectUUIDs) => {
  let objectUUIDFields = {};
  promise
    .map(
      objectUUIDs,
      async (objectUUID) => {
        const fields = ((await getObjectFieldDetails(objectUUID)) || []).map((field) => field.Name);
        objectUUIDFields[objectUUID] = fields;
      },
      { concurrency: 10 }
    )
    .catch((err) => this.log.error('Error Occurred while fetching object fields', err));
  return objectUUIDFields;
};

/**
 * Fetch Object Field Details
 */
export const getTabularReferenceObjectFields = async (objectUUID, fieldUUID, params = {}) => {
  try {
    const objectFields = await axios.$get(`/api/object/tabular-reference/fields/list/${objectUUID}/${fieldUUID}`, {
      params
    });

    return modifyObjectFields(objectFields);
  } catch (error) {
    toast.error(errorHandler('common.object_record.fetch_object_fields', error));
    return undefined;
  }
};

/**
 * Fetch Object Details
 */
export const getEntityList = async (params) => {
  try {
    return await axios.$get(`/api/entitytrees/list`, { params });
  } catch (error) {
    toast.error(
      errorHandler(
        i18n.global.t('common.dynamic_labels.fetch_action', {
          feature: i18n.global.t('common.feature.entity')
        }),
        error
      )
    );
    return [];
  }
};

// Get only authorized entity list.
export const getAuthorizedEntityList = async (params) => {
  try {
    return await axios.$get(`/api/entitytrees/list/current`, { params });
  } catch (error) {
    toast.error(
      errorHandler(
        i18n.global.t('common.dynamic_labels.fetch_action', {
          feature: i18n.global.t('common.feature.entity')
        }),
        error
      )
    );
    return [];
  }
};

/**
 * Fetch reference field's values as per specified filters
 */
export const fetchReferenceFieldValues = async (
  { fieldUUID, objectUUID },
  {
    filterSearch = null,
    selectedValue = null,
    fieldFilters = [],
    enableSearch = true,
    tenantEntryUUID = null,
    fetchTotalCount,
    pageNo,
    throwOnError = false
  } = {},
  abortController = null
) => {
  try {
    const customUrl = tenantEntryUUID
      ? `/object/${objectUUID}/key-value/${fieldUUID}`
      : `/api/object/${objectUUID}/key-value/${fieldUUID}`;

    return await axios.$post(
      customUrl,
      {
        enableSearch,
        fieldFilters: encodeSingleFieldToBase64(JSON.stringify(fieldFilters)),
        filterSearch: encodeSingleFieldToBase64(filterSearch),
        selectedValue: selectedValue
      },
      {
        params: {
          tenantEntryUUID,
          fetchTotalCount,
          ...(pageNo !== undefined && { pageNo })
        },
        signal: abortController?.signal
      }
    );
  } catch (error) {
    if (error?.message !== 'canceled' && error?.code !== 'ERR_CANCELED') {
      toast.error(errorHandler('common.object_record.fetch_referenced_objects_records', error));
    }
    if (throwOnError) {
      throw error;
    }
    return [];
  }
};

export const getObjectSelectValues = async (params) => {
  try {
    const objectDetails = await axios.$get('/api/object/select-values', { params });
    return objectDetails && objectDetails.length ? objectDetails[0] : {};
  } catch (error) {
    toast.error(errorHandler(i18n.global.t('common.object_record.fetch_object_details'), error));
    return {};
  }
};
